import React from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";

import { withTranslation } from "react-i18next";

import CardinalityOptionsField from "./Field/CardinalityOptionsField";
import InnerLoader from "../Shared/Loader/InnerLoader";
import ErrorMessage from "../Shared/Error/ErrorMessage";
import { OverlayLoader } from "../Shared/Loader/OverlayLoader"
import { ATTRIBUTE_LABEL } from "../Helpers/Labels/AttributeLabel";

import { createEntityRelation, createReferenceAttribute } from "../../API/EntityRelations/EntityRelations";
import { getAttributes } from "../../API/Attribute/Attribute";
import { getProject, updateProject } from "../../API/Project/project";
import { checkIfContradictiveRelationsExist } from "./utils";
import { getCurrentSubscriptionForOrganisation } from "../../API/Payment/Payment";

class CreateLinkDialog extends React.Component {

	createBtn = React.createRef();
	createAndNewBtn = React.createRef();

	entityAttributeMap = {}

	constructor(props) {
		super(props);
		this.state = {
			subscription: {},
			relationIdentifier: "",
			relationIdentifierChangedByUser: false,
			fromEntity: {},
			cardinality: "1-1",
			toEntity: {},
			showReferenceAttribute: false,
			preventReferenceAttribute: false,
			isRequired: false,
			referenceAttribute: {},
			attributesLoading: true,
			creatingRelation: false,
			allAttributes: [],
			attributes: [],
			contradictiveRelationError: {
				isValid: true,
				message: ""
			},
			showDuplicateIdentifierError: false,
			isAccessedFromWorkspace: true
		};
	}

	async componentDidMount() {
		const subscription = await getCurrentSubscriptionForOrganisation(this.props.organizationId);
		this.setState({ subscription });
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.props.visible !== prevProps.visible) {
			this.setState({
				contradictiveRelationError: {
					isValid: true,
					message: ""
				}
			});
		}
	}

	render() {
		// if not visible do not show dialog
		const { visible } = this.props;
		if (!visible) {
			return null;
		}

		const { isAccessedFromWorkspace, t } = this.props;

		return (
			<Dialog
				autoFocus={true}
				title={
					<>
						{t("title", { ns: "linked_entity/create_link_dialog" })}
						{
							this.state.isReferenceAttributeExceedingLimit &&
							<span className="text-danger ms-2">
								{t("errors.max_attributes_exceeded", {
									ns: "linked_entity/create_link_dialog",
									limit: this.state.subscription?.maxAttributesPerApp,
									entitiesWithAttributeLimitExceeded: this.state.toEntity?.name
								})}
							</span>
						}
					</>
				}
				width={`${isAccessedFromWorkspace ? '90%' : '60%'}`}
				height={'80%'}
				onClose={this.closeDialog}
			>
				<form onSubmit={this.create} onKeyDown={(e) => {
					if (e.key === "Enter" && !this.shouldCreateBeDisabled())
						this.create(e)
				}} className="p-0 d-flex flex-column h-100 pt-4">
					<div className="d-inline-block mb-2">
						<span className="form-label me-2">
							{t("form.link_message", { ns: "linked_entity/create_link_dialog" })}
						</span>
						<ErrorMessage
							valid={this.state.contradictiveRelationError.isValid}
							error={this.state.contradictiveRelationError.message}
						/>
					</div>
					<div className="mt-1 d-center">
						<DropDownList
							style={{ width: "100%" }}
							className="form-control-custom"
							data={this.getFromEntities()}
							textField="name"
							name="fromEntity"
							onChange={this.onChange}
							value={this.state.fromEntity}
						/>

						<div className="px-4 d-flex justify-content-center align-items-center">
							<div className="pointer" onClick={this.swapEntities}>
								<i className="fal fa-exchange h4"></i>
							</div>
						</div>

						<DropDownList
							style={{ width: "100%" }}
							className="form-control-custom"
							data={this.getToEntities()}
							textField="name"
							name="toEntity"
							onChange={this.onChange}
							value={this.state.toEntity}
						/>
					</div>
					{
						this.state.fromEntity._id && this.state.toEntity._id &&
						<div className="my-1" style={{ width: "27%" }}>
							<label className="form-label" htmlFor="relation-identifier-field">
								{t("form.relation_name_label", { ns: "linked_entity/create_link_dialog" })}
								{
									this.state.showDuplicateIdentifierError &&
									<span className="text-danger">
										&nbsp;{t("form.relation_identifier_used", { ns: "linked_entity/create_link_dialog" })}
									</span>
								}
							</label>
							<input
								name="relationIdentifier"
								className="form-control"
								id="relation-identifier-field"
								// placeholder="Enter a value you could use to identify this relation"
								onChange={this.handleIdentifierChange}
								value={this.state.relationIdentifier}
								maxLength={64} />
						</div>
					}

					<div className="flex-grow-1">
						{this.state.fromEntity._id && this.state.toEntity._id && <CardinalityOptionsField firstEntityName={this.state.fromEntity.name} secondEntityName={this.state.toEntity.name} selected={this.state.cardinality} onSelect={this.handleCardinality} showCardinalityLabel={true} />}

						{this.state.cardinality === "1-*" && (
							<>
								{
									!this.state.attributesLoading &&
									this.state.attributes &&
									this.state.attributes.length > 0 &&
									!this.state.preventReferenceAttribute &&
									<label htmlFor="refAttr" disabled={this.state.preventReferenceAttribute}>
										<Checkbox
											id="refAttr"
											name="showReferenceAttribute"
											checked={this.state.showReferenceAttribute}
											onChange={this.handleCheckbox}
											disabled={this.state.attributes.length === 0 ? true : this.state.preventReferenceAttribute}
										/>
										<span className="checkbox-label">
											<span>{t("form.reference_attribute.label", { ns: "linked_entity/create_link_dialog" })}</span>
											<span className="txt-link"> {this.state.toEntity.name}</span>&nbsp;
											<span>?</span>
										</span>
									</label>
								}

								{
									this.state.attributes.length > 0 &&
									this.state.showReferenceAttribute &&
									(
									<div className="d-flex align-items-center w-50">
										<DropDownList
											style={{ width: "100%" }}
											data={this.state.attributes}
											textField="attributeName"
											name="referenceAttribute"
											onChange={this.onChange}
											value={this.state.referenceAttribute}
										/>

										<span htmlFor="isRequired" className="text-nowrap">
											<Checkbox
												id="isRequired"
												name="isRequired"
												className="mx-2"
												checked={this.state.isRequired}
												onChange={this.handleCheckbox}
												label={t("form.required_label", { ns: "linked_entity/create_link_dialog" })}
											/>
										</span>
									</div>
								)}
							</>
						)}
					</div>
				</form>
				<DialogActionsBar layout="end">
					<div className="mb-3 me-3">
						<small className="mt-1">
							{this.state.creatingRelation && (
								<div className="d-inline-block">
									<InnerLoader borderColor="green" />
									<span className="me-4 text-success"></span>
								</div>
							)}
						</small>
						<button data-testid="cancel-btn" type="button" onClick={this.closeDialog} className="btn btn-secondary">
							{t("buttons.cancel", { ns: "linked_entity/create_link_dialog" })}
						</button>
						<button data-testid="create-and-new-btn" type="button" onClick={this.createAndReset} ref={this.createAndNewBtn} disabled={this.shouldCreateBeDisabled()} className="mx-2 btn btn-primary">
							{t("buttons.create_and_new", { ns: "linked_entity/create_link_dialog" })}
						</button>
						<button data-testid="create-btn" type="button" ref={this.createBtn} onClick={this.create} disabled={this.shouldCreateBeDisabled()} className="btn btn-primary">
							{t("buttons.create", { ns: "linked_entity/create_link_dialog" })}
						</button>
					</div>
				</DialogActionsBar>
				{this.state.creatingRelation &&
					<OverlayLoader />
				}
			</Dialog >
		);
	}

	getFromEntities = () => {
		if (this.state.toEntity && this.state.toEntity.isUserEntity) {
			return this.props.entities.filter(entity => !entity.isUserEntity);
		}
		return this.props.entities;
	}

	getToEntities = () => {
		if (this.state.fromEntity && this.state.fromEntity.isUserEntity) {
			return this.props.entities.filter(entity => !entity.isUserEntity);
		}
		return this.props.entities;
	}

	onChange = (e) => {
		const { name, value } = e.target;
		this.setState(
			{
				[name]: value,
			},
			() => {
				const exists = name === "fromEntity" || name === "toEntity" ? this.checkIfContradictiveRelationExistsFor(this.state.cardinality) : false;
				let contradictiveRelationError = {};
				if (exists) {
					contradictiveRelationError.isValid = false
					contradictiveRelationError.message = this.props.t("errors.contradictive_relation", { ns: "linked_entity/create_link_dialog" })
				} else {
					contradictiveRelationError.isValid = true
					contradictiveRelationError.message = ""
				}
				if (name === "fromEntity" || name === "toEntity") {
					this.setState({ contradictiveRelationError });
				}
				if ((name === "fromEntity") && this.state.cardinality === "1-*" && this.state.toEntity._id !== undefined) {
					this.setState({ attributesLoading: true });
					this.getAttributes();
				}
				if (
					(name === "fromEntity" || name === "toEntity") &&
					this.state.cardinality === "1-*"
				) {
					this.validateReferenceAttributeCreation();
				}
				if ((name === "fromEntity" || name === "toEntity") && !this.state.relationIdentifierChangedByUser) {
					const relationIdentifier = this.getDefaultRelationIdentifier();
					this.setState({ relationIdentifier });
				}
			}
		);
	};

	handleIdentifierChange = (e) => {
		const newValue = e.target.value ? e.target.value.slice(0, e.target.maxLength) : "";
		const relationWithSameIdentifier = this.props.relations.findIndex(relation => (
			relation.relationIdentifier === newValue
		)) > -1;
		this.setState({
			relationIdentifier: newValue,
			relationIdentifierChangedByUser: true,
			showDuplicateIdentifierError: relationWithSameIdentifier
		})
	}

	getDefaultRelationIdentifier = () => {
		const { fromEntity, toEntity } = this.state;
		if (!fromEntity._id || !toEntity._id) return "";
		const { relations } = this.props;
		const relationsBetweenSameEntities = relations.filter(relation => (
			relation.fromEntity._id === fromEntity._id && relation.toEntity._id === toEntity._id
		));
		const numRelationsBetweenSameEntities = relationsBetweenSameEntities.length;
		const identifier = fromEntity.name + "_" + toEntity.name;
		return numRelationsBetweenSameEntities === 0 ? identifier : identifier + numRelationsBetweenSameEntities;
	}

	swapEntities = (e) => {
		let { fromEntity, toEntity, contradictiveRelationError } = this.state;
		const temp = fromEntity;
		fromEntity = toEntity;
		toEntity = temp;
		this.setState(
			{
				fromEntity,
				toEntity,
			},
			() => {
				const exists = this.checkIfContradictiveRelationExistsFor(this.state.cardinality);

				if (exists) {
					contradictiveRelationError.isValid = false
					contradictiveRelationError.message = this.props.t("errors.contradictive_relation", { ns: "linked_entity/create_link_dialog" })
				} else {
					contradictiveRelationError.isValid = true
					contradictiveRelationError.message = ""
				}
				this.setState({ contradictiveRelationError });
				if (this.state.cardinality === "1-*") {
					this.getAttributes();
					this.validateReferenceAttributeCreation();
				}
				this.setState({ contradictiveRelationError });
				if (!this.state.relationIdentifierChangedByUser) {
					const relationIdentifier = this.getDefaultRelationIdentifier();
					this.setState({ relationIdentifier });
				}
			}
		);
	};

	handleCardinality = (newValue) => {
		let {
			contradictiveRelationError,
			showReferenceAttribute,
			referenceAttribute
		} = this.state
		if (newValue === "1-*") {
			this.getAttributes();
			this.validateReferenceAttributeCreation();
		}
		if (showReferenceAttribute === true) {
			showReferenceAttribute = false;
			referenceAttribute = {};
		}
		const exists = this.checkIfContradictiveRelationExistsFor(newValue);
		if (exists) {
			contradictiveRelationError.isValid = false
			contradictiveRelationError.message = this.props.t("errors.contradictive_relation", { ns: "linked_entity/create_link_dialog" })
		} else {
			contradictiveRelationError.isValid = true
			contradictiveRelationError.message = ""
		}
		this.setState({
			attributesLoading: true,
			cardinality: newValue,
			contradictiveRelationError,
			showReferenceAttribute,
			referenceAttribute
		});
	};

	checkIfContradictiveRelationExistsFor = (cardinality) => {
		if (cardinality === "1-*") {
			const { relations } = this.props;
			const {
				fromEntity: { _id: fromEntityId },
				toEntity: { _id: toEntityId },
			} = this.state;
			return checkIfContradictiveRelationsExist(
				this.props.relations,
				{
					fromEntity: { _id: fromEntityId },
					toEntity: { _id: toEntityId },
					cardinality: "1-*",
				}
			)
		}
		return false;
	};

	handleCheckbox = (e) => {
		const { name } = e.target.element;
		this.setState({
			[name]: e.value,
		}, () => {
			if (name === "showReferenceAttribute") {
				this.validateReferenceAttributeExceedsSubscriptionLimit();
			}
		});
	};

	shouldCreateBeDisabled = () => {
		const {
			fromEntity, toEntity, relationIdentifier,
			showReferenceAttribute, referenceAttribute,
			relationIdentifierChangedByUser, showDuplicateIdentifierError,
			contradictiveRelationError, isReferenceAttributeExceedingLimit
		} = this.state;
		if (!fromEntity._id || !toEntity._id) {
			return true;
		}
		if (!contradictiveRelationError.isValid) {
			return true;
		}
		if (showReferenceAttribute && !referenceAttribute._id) {
			return true;
		}
		if (relationIdentifierChangedByUser) {
			return showDuplicateIdentifierError || !relationIdentifier?.trim();
		}
		if (isReferenceAttributeExceedingLimit) {
			return true;
		}
		return false;
	};

	closeDialog = () => {
		this.reset();
		this.props.toggle(false);
	};

	reset = () => {
		this.setState({
			fromEntity: {},
			cardinality: "1-1",
			toEntity: {},
			showReferenceAttribute: false,
			isRequired: false,
			preventReferenceAttribute: false,
			referenceAttribute: {},
			attributesLoading: true,
			attributes: [],
			creatingRelation: false,
			showDuplicateIdentifierError: false,
			relationIdentifier: "",
			relationIdentifierChangedByUser: false,
		});
	};

	validateReferenceAttributeCreation = () => {
		const { relations } = this.props;
		const { fromEntity, toEntity } = this.state;
		const preventReferenceAttribute = (
			relations.findIndex((link) => (
				link.fromEntity._id === fromEntity._id &&
				link.toEntity._id === toEntity._id
			)) > -1 || fromEntity.isUserEntity
		);
		this.setState({ preventReferenceAttribute });
	};

	validateReferenceAttributeExceedsSubscriptionLimit = async () => {
		const { entityAttributeMap } = this;
		const { toEntity, showReferenceAttribute, subscription } = this.state;
		if (showReferenceAttribute) {
			let attributes = entityAttributeMap[toEntity._id];
			if (!attributes) {
				attributes = await getAttributes(toEntity._id);
				entityAttributeMap[toEntity._id] = attributes;
			}
			const nonSystemAttributes = attributes.filter(attr => !attr.isSystemAttribute);
			const limit = subscription?.maxAttributesPerApp;
			this.setState({ isReferenceAttributeExceedingLimit: nonSystemAttributes.length + 1 > limit });
		} else {
			this.setState({ isReferenceAttributeExceedingLimit: false });
		}
	}

	getOrder = () => {
		if (this.props.relations.length === 0) {
			return 10000000000;
		}

		return parseInt(this.props.relations[this.props.relations.length - 1].order) + 10000000000;
	};

	generateSystemRelationName = async () => {
		const { projectId } = this.props;
		const { fromEntity, toEntity } = this.state;

		let sysRelationName = "";
		let sysReverseRelationName = "";

		const currentEntityName = fromEntity.sysName;
		const linkingEntityName = toEntity.sysName;

		sysRelationName = linkingEntityName + currentEntityName;
		sysReverseRelationName = currentEntityName + linkingEntityName;

		const project = await getProject(projectId);
		let numberOfLinkedEntities = ++project.numberOfLinkedEntities;

		sysRelationName += numberOfLinkedEntities;
		numberOfLinkedEntities++;

		sysReverseRelationName += numberOfLinkedEntities;
		numberOfLinkedEntities++;

		project.numberOfLinkedEntities = numberOfLinkedEntities;

		updateProject(projectId, { numberOfLinkedEntities})

		return [sysRelationName, sysReverseRelationName];
	};

	generateReverseRelationName = () => {
		const { fromEntity } = this.state;
		const { relations } = this.props;
		const initialName = fromEntity.name;
		let counter = 0;
		let reverseRelationName = initialName;
		for (let relationIndex = 0; relationIndex < relations.length; relationIndex++) {
			if (relations[relationIndex].reverseRelationName === reverseRelationName) {
				counter++;
				reverseRelationName = initialName + counter;
			}
		}
		return reverseRelationName;
	};

	generateRelationName = () => {
		const { toEntity } = this.state;
		const { relations } = this.props;
		const initialName = toEntity.name;
		let counter = 0;
		let relationName = initialName;
		for (let relationIndex = 0; relationIndex < relations.length; relationIndex++) {
			if (relations[relationIndex].relationName === relationName) {
				counter++;
				relationName = initialName + counter;
			}
		}
		return relationName;
	};

	getAttributes = async () => {
		try {
			debugger;
			const { _id: targetEntityId } = this.state.fromEntity;
			let allAttributes = this.entityAttributeMap[targetEntityId];
			if (!allAttributes) {
				allAttributes = await getAttributes(targetEntityId);
			}
			const attributes = allAttributes.filter((attr) => attr.attributeType === "String" && !attr.isSystemAttribute);
			const attributesLoading = false;
			const referenceAttribute = attributes[0] || {};
			const isRequired = false;
			this.setState({
				allAttributes,
				attributes,
				attributesLoading,
				referenceAttribute,
				isRequired,
				// isReferenceAttributeExceedingLimit is set to true to prevent the user from creating a reference attribute
				// while validating if the reference attribute exceeds the subscription limit
				// the function validateReferenceAttributeExceedsSubscriptionLimit will set this to false if the limit is not exceeded
				isReferenceAttributeExceedingLimit: true
			}, () => {
				this.validateReferenceAttributeExceedsSubscriptionLimit();
			});
		} catch (error) {
			this.setState({
				error,
			});
		}
	};

	// generateSystemRelationName = async (type = "normal") => {
	//     const { projectId } = this.props;
	//     const { fromEntity, toEntity } = this.state;

	//     let newName = "";

	//     const currentEntityName = fromEntity.sysName;
	//     const linkingEntityName = toEntity.sysName;

	//     if (type === "reversed") {
	//         newName = linkingEntityName + currentEntityName;
	//     } else {
	//         newName = currentEntityName + linkingEntityName;
	//     }

	//     const project = await getNode(projectId);
	//     const numberOfLinkedEntities = ++project.numberOfLinkedEntities;
	//     project.numberOfLinkedEntities = numberOfLinkedEntities;
	//     await updateNode("Project", projectId, project);

	//     newName += String(numberOfLinkedEntities);

	//     return newName;
	// };

	create = async (e, closeDialog = true) => {
		e.preventDefault();

		this.setState({
			creatingRelation: true,
		});

		const { createBtn, createAndNewBtn, reset } = this;
		const { toggle, relations, onCreate, updateLink, projectId } = this.props;
		const { fromEntity, toEntity, cardinality, showReferenceAttribute, relationIdentifier, referenceAttribute } = this.state;

		createBtn.current.disabled = true;
		createAndNewBtn.current.disabled = true;

		//console.time("Create Link: ");

		const [sysRelationName, sysReverseRelationName] = await this.generateSystemRelationName();
		const reverseRelationName = toEntity.name;
		const relationName = fromEntity.name;
		const order = this.getOrder();
		const shouldDeleteOrphanRecords = "false";

		// const sysRelationName = await this.generateSystemRelationName();
		// const relationName = this.generateRelationName();
		// const sysReverseRelationName = await this.generateSystemRelationName(
		//     "reversed"
		// );
		// const reverseRelationName = this.generateReverseRelationName();
		// const order = this.getOrder();
		// const shouldDeleteOrphanRecords = false;

		const properties = {
			fromEntityId: fromEntity._id,
			toEntityId: toEntity._id,
			cardinality,
			sysRelationName,
			relationName,
			sysReverseRelationName,
			reverseRelationName,
			order,
			showReferenceAttribute: showReferenceAttribute,
			identifier: relationIdentifier
		};

		// const linkRequest = await callApiWithAuthorization("/api/v0/project/" + projectId + "/relationAndConfig", {
		// 	method: "POST",
		// 	headers: {
		// 		"Content-Type": "application/json",
		// 	},
		// 	body: JSON.stringify({
		// 		fromEntity,
		// 		toEntity,
		// 		projectId,
		// 		organizationId: this.props.organizationId,
		// 		properties,
		// 	}),
		// });
		const link = await createEntityRelation(projectId, this.props.organizationId, properties);
		debugger;

		if (link.status && link.status === "Failure") {
			if (link.message === "Max Relations allowed limit reached") {
				reset();
				this.setState({
					creatingRelation: false
				})
				toggle(true)
			} else if (link.message === "Relation identifier is used.") {
				this.setState({
					showDuplicateIdentifierError: true,
					creatingRelation: false
				})
				createBtn.current.disabled = false;
				createAndNewBtn.current.disabled = false;
			}
		} else {
			const relation = {
				...link,
				relationId: link._id,
				_id: relations.length + 1,
				fromEntity,
				toEntity,
				relationIdentifier
			};

			if (cardinality === "1-1") {
				relation.deleteLinkingEntityOrphanRecord = properties.deleteLinkingEntityOrphanRecord === "true";
			}

			if (this.state.cardinality === "1-*" && this.state.showReferenceAttribute) {
				const referenceAttribute = await this.createReferencedAttribute(link._id);
				if (referenceAttribute.status === "Failure") {
					relation.referencedAttribute = {
						attributeName: `Select ${ATTRIBUTE_LABEL}`,
						id: "",
						rel_id: "",
					};
					relation.lookupAttribute = {
						id: "",
						required: false,
					};
					relation.showReferenceAttribute = false;
				} else {
					relation.referencedAttribute = this.state.referenceAttribute;
					relation.lookupAttribute = referenceAttribute;
					relation.isLookupRequired = relation.lookupAttribute.required;
					relation.showReferenceAttribute = true;
				}
			} else if (properties.cardinality === "1-*") {
				relation.referencedAttribute = {
					attributeName: `Select ${ATTRIBUTE_LABEL}`,
					id: "",
					rel_id: "",
				};
				relation.lookupAttribute = {
					id: "",
					required: false,
				};
			}

			console.timeEnd("Create Link: ");

			await onCreate(relation);

			reset();
			if (closeDialog) {
				toggle(false);
			}
		}
	};

	createReferencedAttribute = async (linkedEntityRelationId) => {
		const { fromEntity, toEntity, referenceAttribute, isRequired } = this.state;
		const { projectId } = this.props;
		// const attributesOfParentEntity = await getAllAttributes(toEntity.id);
		// let sysName = referenceAttribute.attributeName
		//     .replace(/[^a-zA-Z0-9]/g, "")
		//     .toLowerCase();

		// let pattern = "^" + sysName + "(\\d+)?$";
		// let regex = new RegExp(pattern, "g");

		// const initialName = sysName;
		// let counter = 0;

		// for (let i = 0; i < attributesOfParentEntity.length; i++) {
		//     if (attributesOfParentEntity[i].sysName === sysName) {
		//         counter++;
		//         sysName = initialName + String(counter);
		//         i = 0;
		//     }
		// }

		// let order = 10000000000;

		// if (attributesOfParentEntity.length > 0) {
		//     order =
		//         order +
		//         parseInt(
		//             attributesOfParentEntity[
		//                 attributesOfParentEntity.length - 1
		//             ].order
		//         );
		// }

		const properties = {
			attributeName: `[REF] ${this.state.referenceAttribute.attributeName}`,
			attributeLabel: this.state.referenceAttribute.attributeLabel,
			primary: "false",
			required: isRequired,
			readOnly: false,
			attributeType: "Entity Lookup",
			group: "",
			linkedEntityId: linkedEntityRelationId,
			entityAttributeRelId: this.state.referenceAttribute.rel_id,
			tooltip: this.state.referenceAttribute.attributeName,
			description: "",
			defaultValue: "",
		};
		const parentEntityId = toEntity._id;
		const referencedAttributeId = this.state.referenceAttribute._id;

		// const request = await callApiWithAuthorization("/api/v0/entity/" + parentEntityId + "/referenceAttributeAndConfig", {
		// 	method: "POST",
		// 	headers: {
		// 		"Content-Type": "application/json",
		// 	},
		// 	body: JSON.stringify({
		// 		properties,
		// 		projectId,
		// 		parentEntityId,
		// 		referencedAttributeId,
		// 		linkedEntityRelationId,
		// 	}),
		// });

		// const attribute = await request.json();

		const attribute = await createReferenceAttribute({
			organizationId: this.props.organizationId,
			projectId: projectId,
			entityRelationId: linkedEntityRelationId,
			parentEntityId: parentEntityId,
			referencedEntityId: fromEntity._id,
			referencedAttributeId: referencedAttributeId,
			attributeProperties: properties
		});

		// let attribute = await createNode("Attribute", {
		//     attributeName: this.state.referenceAttribute.attributeName,
		//     attributeLabel: this.state.referenceAttribute.attributeLabel,
		//     primary: false,
		//     required: isRequired,
		//     readOnly: false,
		//     attributeType: "Entity Lookup",
		//     group: "",
		//     sysName: sysName,
		//     linkedEntityId: linkedEntityRelationId,
		//     entityAttributeRelId: this.state.referenceAttribute.rel_id,
		//     tooltip: "",
		//     description: "",
		//     defaultValue: ""
		// });

		// callApiWithAuthorization("/api/v0/nodes/createAttributeRoles", {
		//     method: "POST",
		//     headers: {
		//         "Content-Type": "application/json",
		//     },
		//     body: JSON.stringify({
		//         projectId: projectId,
		//         entityId: toEntity.id,
		//         attributeId: attribute.id,
		//     }),
		// });

		return attribute;
	};

	createAndReset = (e) => {
		this.create(e, false);
	};
}

export default withTranslation("linked_entity/create_link_dialog")(CreateLinkDialog);